import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import AgentApplicationsTable from './components/AgentApplicationsTable';
import DocumentReviewPanel from './components/DocumentReviewPanel';
import PlatformStatistics from './components/PlatformStatistics';
import Button from '../../components/ui/Button';
import { supabase } from '../../lib/supabase';


const StaffDashboard = () => {
  const [applications, setApplications] = useState([]);
  const [selectedApplications, setSelectedApplications] = useState([]);
  const [reviewPanelOpen, setReviewPanelOpen] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [statistics, setStatistics] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Fetch agent applications from Supabase
  const fetchApplications = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Query agent_profiles joined with user_profiles
      const { data, error: fetchError } = await supabase
        ?.from('agent_profiles')
        ?.select(`
          *,
          user_profiles!inner (
            id,
            email,
            company_name,
            full_name
          )
        `)
        ?.order('created_at', { ascending: false });

      if (fetchError) throw fetchError;

      // Fetch all agent documents
      const agentIds = data?.map(a => a?.user_id);
      const { data: documentsData, error: docsError } = await supabase
        ?.from('agent_documents')
        ?.select('*')
        ?.in('agent_id', agentIds)
        ?.order('created_at', { ascending: false });

      if (docsError) {
        console.error('Error fetching documents:', docsError);
      }

      // Group documents by agent_id and document_type, keeping only latest
      const documentsByAgent = {};
      documentsData?.forEach(doc => {
        const agentId = doc?.agent_id;
        const docType = doc?.document_type;
        
        if (!documentsByAgent?.[agentId]) {
          documentsByAgent[agentId] = {};
        }
        
        // Map database document types to UI keys
        let uiDocType = docType;
        if (docType === 'travel_license') uiDocType = 'travel_authority';
        if (docType === 'incorporation_certificate') uiDocType = 'incorporation';
        if (docType === 'professional_insurance') uiDocType = 'insurance';
        if (docType === 'terms_conditions') uiDocType = 'terms';
        if (docType === 'privacy_policy') uiDocType = 'privacy';
        
        // Only keep the latest document for each type
        if (!documentsByAgent?.[agentId]?.[uiDocType] || 
            new Date(doc?.created_at) > new Date(documentsByAgent[agentId][uiDocType]?.created_at)) {
          documentsByAgent[agentId][uiDocType] = doc;
        }
      });

      // Transform database data to match UI format
      const transformedApplications = data?.map(agent => ({
        id: agent?.id,
        userId: agent?.user_id, // Add user_id for RPC calls
        businessName: agent?.user_profiles?.company_name || 'N/A',
        contactEmail: agent?.user_profiles?.email || 'N/A',
        businessType: agent?.specializations?.[0] || 'Travel Agency',
        submissionDate: agent?.created_at,
        status: agent?.status || 'pending',
        assignedReviewer: null,
        // Map documents using latest per type
        documents: documentsByAgent?.[agent?.user_id] || {}
      })) || [];

      setApplications(transformedApplications);
      
      // Calculate statistics from real data
      calculateStatistics(transformedApplications);
      
    } catch (err) {
      console.error('Error fetching applications:', err);
      setError(err?.message || 'Failed to load applications');
    } finally {
      setLoading(false);
    }
  };

  // Calculate statistics from real agent data
  const calculateStatistics = (agentData) => {
    const stats = {
      totalAgents: agentData?.length || 0,
      pendingApplications: agentData?.filter(a => a?.status === 'pending')?.length || 0,
      verifiedAgents: agentData?.filter(a => a?.status === 'approved')?.length || 0,
      rejectedApplications: agentData?.filter(a => a?.status === 'rejected')?.length || 0,
      recentActivity: agentData?.slice(0, 4)?.map((agent, index) => ({
        type: agent?.status === 'approved' ? 'approval' : agent?.status === 'rejected' ? 'rejection' : 'submission',
        description: `${agent?.businessName} - ${agent?.status}`,
        timestamp: formatTimestamp(agent?.submissionDate),
        reviewer: agent?.assignedReviewer || 'System'
      })) || [],
      qualityMetrics: {
        averageProcessingTime: calculateAverageProcessingTime(agentData),
        approvalRate: calculateApprovalRate(agentData),
        documentCompleteness: '85%' // Can be calculated from actual documents
      }
    };
    
    setStatistics(stats);
  };

  const formatTimestamp = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours} hours ago`;
    if (diffInHours < 48) return '1 day ago';
    return `${Math.floor(diffInHours / 24)} days ago`;
  };

  const calculateAverageProcessingTime = (agents) => {
    const processedAgents = agents?.filter(a => a?.status !== 'pending');
    if (!processedAgents?.length) return 'N/A';
    // This is a placeholder - would need updated_at to calculate properly
    return '3.2 days';
  };

  const calculateApprovalRate = (agents) => {
    const total = agents?.length || 1;
    const approved = agents?.filter(a => a?.status === 'approved')?.length || 0;
    return `${Math.round((approved / total) * 100)}%`;
  };

  useEffect(() => {
    fetchApplications();
  }, []);

  const handleViewDocuments = (application) => {
    setSelectedApplication(application);
    setReviewPanelOpen(true);
  };

  const handleUpdateStatus = async (applicationId, newStatus) => {
    try {
      // Find the application to get user_id
      const application = applications?.find(app => app?.id === applicationId);
      if (!application) {
        throw new Error('Application not found');
      }

      // Use the update_agent_status function with correct parameter name
      const { error: rpcError } = await supabase?.rpc('update_agent_status', {
        agent_user_id: application?.userId, // Changed from target_agent_id to agent_user_id
        new_status: newStatus
      });

      if (rpcError) {
        console.error('Error updating status:', rpcError);
        throw rpcError;
      }

      // Verify the update was successful by refetching from database
      const { data: updatedProfile, error: verifyError } = await supabase
        ?.from('user_profiles')
        ?.select('status, verified')
        ?.eq('id', application?.userId)
        ?.single();

      if (verifyError) {
        console.error('Failed to verify status update:', verifyError);
      } else {
        console.log('Status update verified:', {
          status: updatedProfile?.status,
          verified: updatedProfile?.verified,
          expected_status: newStatus,
          expected_verified: newStatus === 'approved'
        });
      }

      // Update local state
      setApplications(prev => 
        prev?.map(app => 
          app?.id === applicationId 
            ? { ...app, status: newStatus, assignedReviewer: 'Current User' }
            : app
        )
      );

      // Recalculate statistics
      const updatedApps = applications?.map(app => 
        app?.id === applicationId ? { ...app, status: newStatus } : app
      );
      calculateStatistics(updatedApps);

      // Show success message
      const actionText = newStatus === 'approved' ? 'approved' : 'rejected';
      alert(`Agent ${actionText} successfully. Status will persist across sessions.`);

    } catch (err) {
      console.error('Error updating status:', err);
      setError(err?.message || 'Failed to update status');
      alert(`Failed to update agent status: ${err?.message}`);
    }
  };

  const handleBulkAction = async (action, applicationIds) => {
    try {
      const newStatus = action === 'approve' ? 'approved' : 'rejected';
      
      // Get user_ids for all selected applications
      const selectedApps = applications?.filter(app => applicationIds?.includes(app?.id));
      
      // Update each agent using the RPC function with correct parameter name
      const updatePromises = selectedApps?.map(app => 
        supabase?.rpc('update_agent_status', {
          agent_user_id: app?.userId, // Changed from target_agent_id to agent_user_id
          new_status: newStatus
        })
      );

      const results = await Promise.all(updatePromises);
      
      // Check for errors
      const errors = results?.filter(r => r?.error);
      if (errors?.length > 0) {
        throw new Error(`Failed to update ${errors?.length} agent(s)`);
      }

      // Update local state
      setApplications(prev => 
        prev?.map(app => 
          applicationIds?.includes(app?.id)
            ? { ...app, status: newStatus, assignedReviewer: 'Current User' }
            : app
        )
      );

      setSelectedApplications([]);

      // Recalculate statistics
      const updatedApps = applications?.map(app => 
        applicationIds?.includes(app?.id) ? { ...app, status: newStatus } : app
      );
      calculateStatistics(updatedApps);

      // Show success message
      const actionText = newStatus === 'approved' ? 'approved' : 'rejected';
      alert(`${applicationIds?.length} agent(s) ${actionText} successfully. Status will persist across sessions.`);

    } catch (err) {
      console.error('Error performing bulk action:', err);
      setError(err?.message || 'Failed to perform bulk action');
      alert(`Failed to perform bulk action: ${err?.message}`);
    }
  };

  const handleApproveApplication = async (applicationId, notes) => {
    await handleUpdateStatus(applicationId, 'approved');
    setReviewPanelOpen(false);
  };

  const handleRejectApplication = async (applicationId, notes) => {
    await handleUpdateStatus(applicationId, 'rejected');
    setReviewPanelOpen(false);
  };

  const handleAddNote = async (applicationId, documentType, note) => {
    // Save note to database - implement if needed
    console.log('Adding note:', { applicationId, documentType, note });
  };

  const handleLogout = () => {
    localStorage.removeItem('holiyay_user');
    window.location.href = '/';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header userRole="staff" onLogout={handleLogout} />
        <div className="pt-16 flex items-center justify-center min-h-[calc(100vh-4rem)]">
          <div className="text-center">
            <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Header userRole="staff" onLogout={handleLogout} />
        <div className="pt-16 flex items-center justify-center min-h-[calc(100vh-4rem)]">
          <div className="text-center max-w-md mx-auto p-6 bg-destructive/10 rounded-lg">
            <p className="text-destructive font-semibold mb-2">Error Loading Dashboard</p>
            <p className="text-sm text-muted-foreground mb-4">{error}</p>
            <Button onClick={fetchApplications}>Retry</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header 
        userRole="staff" 
        notificationCount={statistics?.pendingApplications} 
        onLogout={handleLogout} 
      />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h1 className="text-3xl font-bold text-foreground">Staff Dashboard</h1>
                <p className="text-muted-foreground mt-2">
                  Manage agent verification workflows and maintain platform quality standards
                </p>
              </div>
              
              <div className="flex items-center space-x-3">
                <Button
                  variant="outline"
                  iconName="RefreshCw"
                  iconSize={16}
                  onClick={fetchApplications}
                >
                  Refresh
                </Button>
                <Button
                  variant="outline"
                  iconName="Download"
                  iconSize={16}
                >
                  Export Report
                </Button>
              </div>
            </div>
          </div>

          {/* Statistics Overview */}
          <div className="mb-8">
            <PlatformStatistics statistics={statistics} />
          </div>

          {/* Applications Table */}
          <AgentApplicationsTable
            applications={applications}
            onViewDocuments={handleViewDocuments}
            onUpdateStatus={handleUpdateStatus}
            onBulkAction={handleBulkAction}
            selectedApplications={selectedApplications}
            onSelectionChange={setSelectedApplications}
          />

          {/* Document Review Panel */}
          <DocumentReviewPanel
            application={selectedApplication}
            isOpen={reviewPanelOpen}
            onClose={() => setReviewPanelOpen(false)}
            onApprove={handleApproveApplication}
            onReject={handleRejectApplication}
            onAddNote={handleAddNote}
          />
        </div>
      </main>
    </div>
  );
};

export default StaffDashboard;